import {
  ALL_LOCALES,
  JourneySchema,
  type I18nString,
  type Journey,
  type JourneyStep,
  type Locale,
  type Timestamp,
} from '@kilat/shared';

/**
 * Pure form <-> Journey mapping + validation for the operator journey editor
 * (`/marketing/journeys`). No React, no Firestore SDK — so it's unit-testable
 * and the editor stays a thin rendering layer over it.
 *
 * The editor tunes the SEEDED journeys (win-back / welcome / trial-nudge): each
 * step's per-locale title + body (`I18nString`), deep link, `afterDays` timing,
 * the journey `frequencyCapPerWeek` + `exitOnReturn`, and — for a `segment_scan`
 * trigger — the `inactiveDays` / `minVisits` audience criteria. It never adds or
 * removes steps, changes channels, or edits incentives (composer territory), so
 * those are carried through untouched from the original journey.
 *
 * `buildJourneyUpdate` assembles a FULL journey object and runs it through
 * `JourneySchema.safeParse` before returning it — the caller only ever persists
 * a schema-valid journey (rule 2 + the task's "validate before save").
 */

/** Per-locale text bucket; every supported locale is a present key (may be ''). */
export type LocaleText = Record<Locale, string>;

export interface StepForm {
  /** Delay in days from enrolment, as a raw input string. */
  readonly afterDays: string;
  /** In-app path the nudge opens (optional; '' = none). */
  readonly deepLink: string;
  readonly title: LocaleText;
  readonly body: LocaleText;
}

export interface JourneyForm {
  readonly name: string;
  readonly frequencyCapPerWeek: string;
  readonly exitOnReturn: boolean;
  readonly steps: readonly StepForm[];
  /** `segment_scan` audience knobs (blank + ignored for other trigger kinds). */
  readonly inactiveDays: string;
  readonly minVisits: string;
}

/** Discriminated build result: a schema-valid journey, or a mappable error key. */
export type BuildResult =
  | { readonly ok: true; readonly journey: Journey }
  | { readonly ok: false; readonly errorKey: JourneyErrorKey; readonly stepIndex?: number };

/** i18n leaf keys under `dashboard.journeys.editor.` that the UI resolves. */
export type JourneyErrorKey =
  | 'errName'
  | 'errFreqCap'
  | 'errAfterDays'
  | 'errTitle'
  | 'errBody'
  | 'errDeepLink'
  | 'errInactiveDays'
  | 'errMinVisits'
  | 'errSchema';

const MAX_AFTER_DAYS = 3650; // 10y — generous UI guard; schema itself only caps step count
const MAX_DEEP_LINK = 512; // JourneyStep.content.deepLink max
const MAX_FREQ_CAP = 14; // JourneySchema.frequencyCapPerWeek max
const MAX_INACTIVE_DAYS = 3650;
const MAX_MIN_VISITS = 100_000;

function localeTextFrom(field: I18nString): LocaleText {
  const out = {} as Record<Locale, string>;
  for (const loc of ALL_LOCALES) {
    const v = (field as Record<string, string | undefined>)[loc];
    out[loc] = typeof v === 'string' ? v : '';
  }
  return out;
}

/** Project a Journey into editable form state (one-way; the editor owns edits). */
export function formFromJourney(journey: Journey): JourneyForm {
  const criteria = journey.trigger.kind === 'segment_scan' ? journey.trigger.criteria : undefined;
  return {
    name: journey.name,
    frequencyCapPerWeek: String(journey.frequencyCapPerWeek),
    exitOnReturn: journey.exitOnReturn,
    steps: journey.steps.map((s) => ({
      afterDays: String(s.afterDays),
      deepLink: s.content.deepLink ?? '',
      title: localeTextFrom(s.content.title),
      body: localeTextFrom(s.content.body),
    })),
    inactiveDays: criteria?.inactiveDays !== undefined ? String(criteria.inactiveDays) : '',
    minVisits: criteria?.minVisits !== undefined ? String(criteria.minVisits) : '',
  };
}

/** '' → 'blank'; a valid in-range integer → the number; anything else → 'invalid'. */
function parseIntField(raw: string, min: number, max: number): 'blank' | 'invalid' | number {
  const trimmed = raw.trim();
  if (trimmed === '') return 'blank';
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > max) return 'invalid';
  return n;
}

/** Build an `I18nString` from a locale bucket: `en` required, others kept iff non-blank. */
function buildI18nString(text: LocaleText): I18nString | null {
  const en = text.en.trim();
  if (!en) return null;
  const value: Record<string, string> = { en };
  for (const loc of ALL_LOCALES) {
    if (loc === 'en') continue;
    const v = text[loc]?.trim();
    if (v) value[loc] = v;
  }
  return value as I18nString;
}

/**
 * Assemble + validate the edited journey. Preserves everything the editor
 * doesn't touch (id, channels, incentives, active, createdAt, non-scan trigger
 * fields). `now` is injected so the function stays pure/testable.
 */
export function buildJourneyUpdate(
  original: Journey,
  form: JourneyForm,
  now: Timestamp,
): BuildResult {
  const name = form.name.trim();
  if (!name || name.length > 80) return { ok: false, errorKey: 'errName' };

  const freqCap = parseIntField(form.frequencyCapPerWeek, 1, MAX_FREQ_CAP);
  if (typeof freqCap !== 'number') return { ok: false, errorKey: 'errFreqCap' };

  const steps: JourneyStep[] = [];
  for (let i = 0; i < original.steps.length; i += 1) {
    const sf = form.steps[i];
    const originalStep = original.steps[i];
    if (!sf || !originalStep) return { ok: false, errorKey: 'errSchema', stepIndex: i };

    const afterDays = parseIntField(sf.afterDays, 0, MAX_AFTER_DAYS);
    if (typeof afterDays !== 'number') return { ok: false, errorKey: 'errAfterDays', stepIndex: i };

    const title = buildI18nString(sf.title);
    if (!title) return { ok: false, errorKey: 'errTitle', stepIndex: i };
    const body = buildI18nString(sf.body);
    if (!body) return { ok: false, errorKey: 'errBody', stepIndex: i };

    const deepLink = sf.deepLink.trim();
    if (deepLink.length > MAX_DEEP_LINK)
      return { ok: false, errorKey: 'errDeepLink', stepIndex: i };

    steps.push({
      afterDays,
      channels: originalStep.channels,
      content: { title, body, ...(deepLink ? { deepLink } : {}) },
      incentive: originalStep.incentive,
    });
  }

  let trigger = original.trigger;
  if (original.trigger.kind === 'segment_scan') {
    const inactive = parseIntField(form.inactiveDays, 1, MAX_INACTIVE_DAYS);
    if (inactive === 'invalid') return { ok: false, errorKey: 'errInactiveDays' };
    const minVisits = parseIntField(form.minVisits, 0, MAX_MIN_VISITS);
    if (minVisits === 'invalid') return { ok: false, errorKey: 'errMinVisits' };

    const base = { ...original.trigger.criteria };
    delete base.inactiveDays;
    delete base.minVisits;
    trigger = {
      kind: 'segment_scan',
      criteria: {
        ...base,
        ...(typeof inactive === 'number' ? { inactiveDays: inactive } : {}),
        ...(typeof minVisits === 'number' ? { minVisits } : {}),
      },
    };
  }

  const journey: Journey = {
    id: original.id,
    name,
    trigger,
    steps,
    exitOnReturn: form.exitOnReturn,
    frequencyCapPerWeek: freqCap,
    active: original.active,
    createdAt: original.createdAt,
    updatedAt: now,
  };

  const parsed = JourneySchema.safeParse(journey);
  if (!parsed.success) return { ok: false, errorKey: 'errSchema' };
  return { ok: true, journey };
}
